import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Rate,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  DownloadOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../config/api';

dayjs.extend(isoWeek);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type Frequency = 'weekly' | 'biweekly' | 'monthly';

function defaultPeriodForFrequency(frequency: Frequency): [Dayjs, Dayjs] {
  if (frequency === 'monthly') {
    const start = dayjs().subtract(1, 'month').startOf('month');
    const end = dayjs().subtract(1, 'month').endOf('month');
    return [start, end];
  }
  if (frequency === 'biweekly') {
    const end = dayjs().subtract(1, 'day').startOf('day');
    const start = end.subtract(13, 'day').startOf('day');
    return [start, end];
  }
  const start = dayjs().startOf('isoWeek').subtract(1, 'week');
  const end = start.endOf('isoWeek');
  return [start, end];
}

interface AgentRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface SampleTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  business_name?: string;
  feedback_has_row?: boolean;
  feedback_is_submitted?: boolean;
  feedback_rating?: number | null;
  feedback_comment?: string | null;
  feedback_submitted_at?: string | null;
}

interface GradeRow {
  strengths: string;
  improvements: string;
  score: number | undefined;
}

const TicketAudit: React.FC = () => {
  const queryClient = useQueryClient();
  const [agentId, setAgentId] = useState<string | undefined>();
  const [period, setPeriod] = useState<[Dayjs, Dayjs] | null>(null);
  const [sampled, setSampled] = useState<SampleTicket[]>([]);
  const [grades, setGrades] = useState<Record<string, GradeRow>>({});
  const [summaryNotes, setSummaryNotes] = useState('');
  const [sampling, setSampling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailReport, setDetailReport] = useState<{ report: any; items: any[] } | null>(null);

  const [exportMonth, setExportMonth] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));
  const [exportAgentId, setExportAgentId] = useState<string>('all');
  const [exporting, setExporting] = useState(false);

  const { data: config, isLoading: configLoading } = useQuery(
    'ticket-audit-config',
    async () => {
      const res = await api.get('/api/ticket-audits/config');
      return res.data as { frequency: Frequency };
    }
  );

  const { data: agentsData } = useQuery(
    'ticket-audit-agents',
    async () => {
      const res = await api.get('/api/users/agents');
      return res.data as { users: AgentRow[] };
    }
  );

  const agents = agentsData?.users || [];

  const frequency = (config?.frequency || 'weekly') as Frequency;

  useEffect(() => {
    if (!configLoading && config && !period) {
      setPeriod(defaultPeriodForFrequency(frequency));
    }
  }, [configLoading, config, frequency, period]);

  const applyFrequencyDefaults = () => {
    setPeriod(defaultPeriodForFrequency(frequency));
    message.info('Period reset to match the configured cadence');
  };

  const runSample = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and audit period');
      return;
    }
    setSampling(true);
    try {
      const res = await api.post('/api/ticket-audits/sample', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
      });
      const tickets: SampleTicket[] = res.data.tickets || [];
      setSampled(tickets);
      const nextGrades: Record<string, GradeRow> = {};
      tickets.forEach((t) => {
        nextGrades[t.id] = { strengths: '', improvements: '', score: undefined };
      });
      setGrades(nextGrades);
      if (tickets.length < (res.data.requested_limit || 5)) {
        message.warning(
          `Only ${tickets.length} ticket(s) matched this agent and period (need activity in range, status other than New).`
        );
      } else {
        message.success(`Loaded ${tickets.length} random tickets`);
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to sample tickets');
    } finally {
      setSampling(false);
    }
  };

  const saveReport = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and period');
      return;
    }
    if (!sampled.length) {
      message.warning('Pick random tickets first');
      return;
    }
    const items = sampled.map((t) => {
      const g = grades[t.id] || { strengths: '', improvements: '', score: undefined };
      return {
        ticket_id: t.id,
        strengths: g.strengths || null,
        improvements: g.improvements || null,
        score: g.score ?? null,
      };
    });
    setSaving(true);
    try {
      await api.post('/api/ticket-audits/reports', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
        summary_notes: summaryNotes || null,
        items,
      });
      message.success('Audit report saved');
      setSampled([]);
      setGrades({});
      setSummaryNotes('');
      queryClient.invalidateQueries('ticket-audit-reports');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const { data: reportsData, isLoading: reportsLoading } = useQuery(
    'ticket-audit-reports',
    async () => {
      const res = await api.get('/api/ticket-audits/reports', { params: { page: 1, limit: 50 } });
      return res.data as { reports: any[]; total: number };
    }
  );

  const openDetail = async (id: string) => {
    try {
      const res = await api.get(`/api/ticket-audits/reports/${id}`);
      setDetailReport(res.data);
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load report');
    }
  };

  const runExport = async () => {
    if (!exportMonth) {
      message.warning('Select a month');
      return;
    }
    const y = exportMonth.year();
    const m = exportMonth.month() + 1;
    setExporting(true);
    try {
      const res = await api.get('/api/ticket-audits/export', {
        params: {
          year: y,
          month: m,
          agent_id: exportAgentId,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-audits-${y}-${String(m).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('Download started');
    } catch (e: any) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const j = JSON.parse(text);
          message.error(j.error || 'Export failed');
        } catch {
          message.error('Export failed');
        }
      } else {
        message.error(e.response?.data?.error || 'Export failed');
      }
    } finally {
      setExporting(false);
    }
  };

  const feedbackBlock = (t: SampleTicket | any) => {
    if (!t.feedback_has_row) {
      return <Text type="secondary">No customer feedback record for this ticket.</Text>;
    }
    if (!t.feedback_is_submitted) {
      return <Text type="warning">Feedback was requested but not submitted yet.</Text>;
    }
    return (
      <Space direction="vertical" size={0}>
        <div>
          <Text type="secondary">Customer rating: </Text>
          {t.feedback_rating != null ? (
            <>
              <Rate disabled allowHalf={false} value={t.feedback_rating} count={5} />
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {t.feedback_rating}/5
              </Tag>
            </>
          ) : (
            <Text type="secondary">Submitted (no numeric rating)</Text>
          )}
        </div>
        {t.feedback_comment ? (
          <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 3, expandable: true }}>
            {t.feedback_comment}
          </Paragraph>
        ) : null}
        {t.feedback_submitted_at && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Submitted {new Date(t.feedback_submitted_at).toLocaleString()}
          </Text>
        )}
      </Space>
    );
  };

  const historyColumns = useMemo(
    () => [
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (v: string) => new Date(v).toLocaleString(),
      },
      {
        title: 'Agent',
        key: 'agent',
        render: (_: any, r: any) =>
          `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim() || '—',
      },
      {
        title: 'Period',
        key: 'period',
        render: (_: any, r: any) => `${r.period_start} → ${r.period_end}`,
      },
      {
        title: 'Auditor',
        key: 'auditor',
        render: (_: any, r: any) =>
          `${r.auditor_first_name || ''} ${r.auditor_last_name || ''}`.trim() || '—',
      },
      { title: 'Tickets', dataIndex: 'item_count', key: 'item_count', width: 80 },
      {
        title: '',
        key: 'act',
        width: 100,
        render: (_: any, r: any) => (
          <Button type="link" size="small" onClick={() => openDetail(r.id)}>
            View
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2}>
        <FileSearchOutlined style={{ marginRight: 12 }} />
        Ticket Audit
      </Title>
      <Paragraph type="secondary">
        Randomly sample tickets for an agent over a period, grade quality (separate from ticket
        records), and use customer feedback as context. Cadence defaults come from Settings.
      </Paragraph>

      <Card title="New audit" style={{ marginBottom: 24 }}>
        {configLoading ? (
          <Text type="secondary">Loading configuration…</Text>
        ) : (
          <Text type="secondary">
            Configured cadence: <Tag>{frequency}</Tag>
            <Button type="link" size="small" onClick={applyFrequencyDefaults}>
              Reset period to match cadence
            </Button>
          </Text>
        )}
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong>Agent</Text>
            <Select
              showSearch
              allowClear
              placeholder="Select agent"
              style={{ width: '100%', marginTop: 8 }}
              optionFilterProp="label"
              value={agentId}
              onChange={(v) => setAgentId(v)}
              options={agents.map((a) => ({
                value: a.id,
                label: `${a.first_name} ${a.last_name} (${a.role})`,
              }))}
            />
          </Col>
          <Col xs={24} md={10}>
            <Text strong>Audit period</Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 8 }}
              value={period || undefined}
              onChange={(r) => setPeriod(r as [Dayjs, Dayjs] | null)}
            />
          </Col>
          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={sampling} onClick={runSample}>
                Pick 5 random tickets
              </Button>
            </Space>
          </Col>
        </Row>

        {sampled.length > 0 && (
          <>
            <Divider />
            <Text strong>Summary notes (optional)</Text>
            <TextArea
              style={{ marginTop: 8, marginBottom: 16 }}
              rows={2}
              value={summaryNotes}
              onChange={(e) => setSummaryNotes(e.target.value)}
              placeholder="Overall notes for this audit session"
            />
            {sampled.map((t) => (
              <Card key={t.id} size="small" style={{ marginBottom: 16 }} title={`#${t.ticket_number} — ${t.subject}`}>
                <Row gutter={16}>
                  <Col xs={24} md={10}>
                    <Text type="secondary">Business: {t.business_name || '—'}</Text>
                    <br />
                    <Text type="secondary">Status: {t.status}</Text>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>
                      Customer feedback
                    </Divider>
                    {feedbackBlock(t)}
                    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                      Use the above when scoring; your grade is recorded only in this audit.
                    </Text>
                  </Col>
                  <Col xs={24} md={14}>
                    <Form layout="vertical">
                      <Form.Item label="What went well">
                        <TextArea
                          rows={2}
                          value={grades[t.id]?.strengths || ''}
                          onChange={(e) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: { ...g[t.id], strengths: e.target.value, improvements: g[t.id]?.improvements || '', score: g[t.id]?.score },
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item label="Needs improvement">
                        <TextArea
                          rows={2}
                          value={grades[t.id]?.improvements || ''}
                          onChange={(e) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: {
                                strengths: g[t.id]?.strengths || '',
                                improvements: e.target.value,
                                score: g[t.id]?.score,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item label="Supervisor score (1–5)">
                        <Rate
                          count={5}
                          value={grades[t.id]?.score}
                          onChange={(v) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: {
                                strengths: g[t.id]?.strengths || '',
                                improvements: g[t.id]?.improvements || '',
                                score: v,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                    </Form>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveReport}>
              Save audit report
            </Button>
          </>
        )}
      </Card>

      <Card title="Monthly export (CSV)" style={{ marginBottom: 24 }}>
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Downloads all audit line items whose <strong>audit period</strong> overlaps the selected
          calendar month. Choose one agent or all agents you are allowed to see.
        </Paragraph>
        <Space wrap align="start">
          <div>
            <Text strong>Month</Text>
            <br />
            <DatePicker
              picker="month"
              style={{ marginTop: 8, minWidth: 160 }}
              value={exportMonth}
              onChange={(v) => setExportMonth(v)}
            />
          </div>
          <div>
            <Text strong>Agent</Text>
            <br />
            <Select
              style={{ width: 260, marginTop: 8 }}
              value={exportAgentId}
              onChange={setExportAgentId}
            >
              <Option value="all">All agents</Option>
              {agents.map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.first_name} {a.last_name}
                </Option>
              ))}
            </Select>
          </div>
          <Button
            style={{ marginTop: 28 }}
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={runExport}
          >
            Download CSV
          </Button>
        </Space>
      </Card>

      <Card title="Recent reports">
        <Table
          rowKey="id"
          loading={reportsLoading}
          dataSource={reportsData?.reports || []}
          columns={historyColumns}
          pagination={false}
          locale={{ emptyText: 'No saved audits yet' }}
        />
      </Card>

      <Modal
        title="Audit report detail"
        open={!!detailReport}
        onCancel={() => setDetailReport(null)}
        footer={null}
        width={800}
      >
        {detailReport && (
          <>
            <Paragraph>
              <Text strong>Agent: </Text>
              {detailReport.report.agent_first_name} {detailReport.report.agent_last_name}
              <br />
              <Text strong>Period: </Text>
              {detailReport.report.period_start} → {detailReport.report.period_end}
              <br />
              <Text strong>Auditor: </Text>
              {detailReport.report.auditor_first_name} {detailReport.report.auditor_last_name}
            </Paragraph>
            {detailReport.report.summary_notes ? (
              <Alert type="info" message={detailReport.report.summary_notes} style={{ marginBottom: 16 }} />
            ) : null}
            {(detailReport.items || []).map((it: any) => (
              <Card key={it.id} size="small" style={{ marginBottom: 12 }} title={`#${it.ticket_number}`}>
                <Paragraph style={{ marginBottom: 8 }}>{feedbackBlock(it)}</Paragraph>
                <Text strong>Strengths: </Text>
                <Paragraph>{it.strengths || '—'}</Paragraph>
                <Text strong>Improvements: </Text>
                <Paragraph>{it.improvements || '—'}</Paragraph>
                <Text strong>Score: </Text> {it.score != null ? `${it.score}/5` : '—'}
              </Card>
            ))}
          </>
        )}
      </Modal>
    </div>
  );
};

export default TicketAudit;
